import React, { Component, Fragment } from 'react';
import Store from 'eas-react-store';
import Header from '../layout/Header/Header';
import { URI } from '../../URI';
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';
import { withTranslation } from 'react-i18next';
import { getWeekdayNames } from '../../i18n/dates';
import { FALLBACK_LANGUAGES } from '../../i18n/languages';
import { fetchAvailableLanguages } from '../../i18n/remoteLanguages';

const FIRST_WEEKDAY = 'first_weekday';
const LANGUAGE = 'language';

class OrganisationSettings extends Component {
    constructor(props) {
        super(props);

        const organisation = this.props.store.User.data.organisation;

        this.state = {
            first_weekday: organisation?.first_weekday || 1,
            language: organisation?.language || null,
            languages: FALLBACK_LANGUAGES,
            // Which row is open for editing, or null. Only one row edits at a time.
            editingField: null,
            saving: false,
            saveSuccess: false,
            saveError: false,
            dropdownOpen: false
        };

        this.onWeekdayChange = this.onWeekdayChange.bind(this);
        this.onLanguageChange = this.onLanguageChange.bind(this);
        this.onDropdownOpen = this.onDropdownOpen.bind(this);
        this.onDropdownClose = this.onDropdownClose.bind(this);
        this.saveSettings = this.saveSettings.bind(this);
        this.handleClickOnCancel = this.handleClickOnCancel.bind(this);

        this._isMounted = false;
    }

    async componentDidMount() {
        this._isMounted = true;

        const languages = await fetchAvailableLanguages();

        if (this._isMounted) {
            this.setState({ languages: languages });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    onWeekdayChange(selectedOption) {
        this.setState({
            first_weekday: parseInt(selectedOption.value),
            saveSuccess: false,
            saveError: false,
            dropdownOpen: false
        });
    }

    onLanguageChange(selectedOption) {
        this.setState({
            language: selectedOption.value,
            saveSuccess: false,
            saveError: false,
            dropdownOpen: false
        });
    }

    onDropdownOpen() {
        this.setState({ dropdownOpen: true });
    }

    onDropdownClose() {
        this.setState({ dropdownOpen: false });
    }

    /**
     * @param {string} field FIRST_WEEKDAY or LANGUAGE
     */
    toggleEditMode(field) {
        const organisation = this.props.store.User.data.organisation;

        this.setState(prevState => ({
            editingField: prevState.editingField === field ? null : field,
            first_weekday: organisation?.first_weekday || 1,
            language: organisation?.language || null,
            dropdownOpen: false
        }));
    }

    handleClickOnCancel() {
        const organisation = this.props.store.User.data.organisation;

        this.setState({
            editingField: null,
            first_weekday: organisation?.first_weekday || 1,
            language: organisation?.language || null,
            saveSuccess: false,
            saveError: false,
            dropdownOpen: false
        });
    }

    componentDidUpdate(prevProps) {
        if (this.state.editingField != null || !this._isMounted) {
            return;
        }

        const organisation = this.props.store.User.data.organisation;
        const prevOrganisation = prevProps.store.User.data.organisation;

        if (organisation == null) {
            return;
        }

        if (!prevOrganisation || organisation.first_weekday !== prevOrganisation.first_weekday) {
            const newFirstWeekday = organisation.first_weekday || 1;

            if (this.state.first_weekday !== newFirstWeekday) {
                this.setState({ first_weekday: newFirstWeekday });
            }
        }

        if (!prevOrganisation || organisation.language !== prevOrganisation.language) {
            const newLanguage = organisation.language || null;

            if (this.state.language !== newLanguage) {
                this.setState({ language: newLanguage });
            }
        }
    }

    async saveSettings(e) {
        if (e) e.preventDefault();

        const field = this.state.editingField;

        if (field == null) {
            return;
        }

        // Only the edited field is sent, so saving one setting never rewrites the other.
        const value = field === LANGUAGE ? this.state.language : this.state.first_weekday;
        const payload = {};
        payload[field] = value;

        this.setState({ saving: true, saveSuccess: false, saveError: false });

        try {
            const updatedOrganisation = await this.props.store.User.updateOrganisationSettings(payload);

            // The API answers a rejected value with a 4xx whose body is not an exception here, so
            // confirm the round-trip actually took rather than reporting a save that did not happen.
            const stored = updatedOrganisation != null ? updatedOrganisation[field] : undefined;
            const accepted = stored !== undefined && String(stored) === String(value);

            if (!this._isMounted) {
                return;
            }

            if (!accepted) {
                this.setState({ saving: false, saveError: true });
                return;
            }

            this.setState({
                saving: false,
                saveSuccess: true,
                editingField: null,
                dropdownOpen: false,
                first_weekday: updatedOrganisation.first_weekday,
                language: updatedOrganisation.language || null
            });
        } catch (error) {
            console.error('Error saving organization settings:', error);

            if (this._isMounted) {
                this.setState({ saving: false, saveError: true });
            }
        }
    }

    /**
     * Weekday names are locale data, not UI copy, so they come from the date layer rather than from
     * a translation file. Options are built in render() because the language can change at runtime.
     * @returns {Array<{value: string, label: string}>}
     */
    getWeekdayOptions() {
        return getWeekdayNames(this.props.i18n.language);
    }

    getFirstDayOfWeekText(value) {
        const options = this.getWeekdayOptions();
        const match = options.find(option => parseInt(option.value) === value);

        return match != null ? match.label : options[0].label;
    }

    /** @returns {Array<{value: string, label: string}>} endonyms, so each reads natively */
    getLanguageOptions() {
        return this.state.languages.map(language => ({ value: language.code, label: language.name }));
    }

    getLanguageText(code) {
        const match = this.getLanguageOptions().find(option => option.value === code);

        return match != null ? match.label : code;
    }

    /**
     * @param {string} field FIRST_WEEKDAY or LANGUAGE
     * @param {object} config label, hint, displayed value, dropdown options and placeholder
     */
    renderRow(field, config) {
        const { saving, dropdownOpen } = this.state;
        const isEditing = this.state.editingField === field;
        const rowClassName = isEditing ? 'category-row edit-mode' : 'category-row';

        return (
            <tr className={rowClassName}>
                <td>
                    <span>{config.label}</span>
                    {config.hint != null &&
                        <span className="setting-hint">{config.hint}</span>
                    }
                </td>
                <td>
                    <span className={`category-title ${config.valueClassName || ''}`}>
                        {config.text}
                    </span>
                    <div className={`edit-input dropdown-wrapper ${config.dropdownClassName || ''}`}>
                        <Dropdown
                            options={config.options}
                            onChange={config.onChange}
                            onOpen={this.onDropdownOpen}
                            onClose={this.onDropdownClose}
                            value={config.selected}
                            placeholder={config.placeholder}
                            className={dropdownOpen ? "dropdown-open" : ""}
                        />
                    </div>
                </td>
                <td className="cell-right">
                    <div className="table-action">
                        <span
                            className="table-action-edit"
                            onClick={() => this.toggleEditMode(field)}>
                                {this.props.t('common.edit')}
                        </span>
                    </div>
                    <div className="btn-wrapper">
                        <button className="btn btn-table-small btn-cancel" onClick={this.handleClickOnCancel}>{this.props.t('common.cancel')}</button>
                        <button
                            className="btn btn-table-small btn-save"
                            onClick={this.saveSettings}
                            disabled={saving}
                        >
                            {saving ? this.props.t('common.saving') : this.props.t('common.save')}
                        </button>
                    </div>
                </td>
            </tr>
        );
    }

    render() {
        const { first_weekday, language, saveSuccess, saveError } = this.state;

        const weekdayOptions = this.getWeekdayOptions();
        const selectedWeekday = weekdayOptions.find(option => parseInt(option.value) === first_weekday) || weekdayOptions[0];

        const languageOptions = this.getLanguageOptions();
        const selectedLanguage = languageOptions.find(option => option.value === language) || null;

        return (
            <Fragment>
                <Header
                    current={URI.admin.organisationSettings}
                    currentRoot={URI.admin.root}
                />
                <div className="container content-container organisation-settings">
                    <div className="category-table-header mb-3">
                        <h1>{this.props.t('nav.organisationSettings')}</h1>
                    </div>

                    {saveSuccess && (
                        <div className="alert alert-success mb-3" role="alert">
                            {this.props.t('organisation.saved')}
                        </div>
                    )}

                    {saveError && (
                        <div className="alert alert-danger mb-3" role="alert">
                            {this.props.t('organisation.saveFailed')}
                        </div>
                    )}

                    <table className="categories-table">
                        <thead>
                            <tr>
                                <th>{this.props.t('common.setting')}</th>
                                <th>{this.props.t('common.value')}</th>
                                <th className="cell-right">{this.props.t('common.action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.renderRow(FIRST_WEEKDAY, {
                                label: this.props.t('organisation.firstWeekday'),
                                text: this.getFirstDayOfWeekText(first_weekday),
                                valueClassName: 'weekday-name',
                                dropdownClassName: 'weekday-dropdown',
                                options: weekdayOptions,
                                selected: selectedWeekday,
                                onChange: this.onWeekdayChange,
                                placeholder: this.props.t('organisation.selectFirstWeekday')
                            })}
                            {this.renderRow(LANGUAGE, {
                                label: this.props.t('organisation.defaultLanguage'),
                                // This is a default for users who have not chosen, never an override.
                                hint: this.props.t('organisation.defaultLanguageHint'),
                                text: this.getLanguageText(language),
                                options: languageOptions,
                                selected: selectedLanguage,
                                onChange: this.onLanguageChange,
                                placeholder: this.props.t('organisation.selectDefaultLanguage')
                            })}
                        </tbody>
                    </table>
                </div>
            </Fragment>
        );
    }
}

export default Store.connectStore(withTranslation()(OrganisationSettings));
